import { ChatDetailsResponse, ChatSettingsFormData } from '@choco-admin/types';
import { Card, CardBody, Switch } from '@heroui/react';
import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FiSettings, FiPower } from 'react-icons/fi';

import { FormFooter } from '../../../../../../components/FormFooter';
import { useNotification } from '../../../../../../contexts/NotificationContext';
import {
  CHAT_TYPE_OPTIONS,
  getChatTypeOptionFromChatType,
} from '../../../../constants/chatOptions';
import { useChatPricesVisibility } from '../../../../hooks/useChatPricesVisibility';
import { useUpdateChatEnabled } from '../../../../hooks/useUpdateChatEnabled';
import { useUpdateChatPricesVisibility } from '../../../../hooks/useUpdateChatPricesVisibility';
import { useUpdateChatSettings } from '../../../../hooks/useUpdateChatSettings';
import { useUpdateSubCatalogChats } from '../../../../hooks/useUpdateSubCatalogChats';
import { useUpdateSupplierBuyerMov } from '../../../../hooks/useUpdateSupplierBuyerMov';
import { resolveCustomerNumberClear } from '../../../../utils/customerNumberClear';
import {
  formatEmailsToString,
  parseEmailsFromString,
} from '../../../../utils/formHelpers';
import {
  InvalidMovAmountError,
  resolveCustomerLevelMov,
} from '../../../../utils/movAmount';
import {
  RequestedSaveOperation,
  runRequestedSaveOperations,
} from '../../../../utils/saveOrchestration';
import {
  commitSubmittedFields,
  resetToSubmitted,
  seedPriceVisibilityBaseline,
} from '../../../../utils/settingsFormBaseline';
import { getSubCatalogChanges } from '../../../../utils/subCatalogSelection';
import {
  ChatFormInputField,
  ChatTypeSelect,
  VisibleChatPricesSection,
  CustomerLevelMovSection,
} from '../../../shared';
import { PageHeader } from '../PageHeader';

import {
  SubCatalogSelect,
  DeliverySettingsSection,
  CustomerNumberWarning,
} from './settings';

interface SettingsTabPanelProps {
  chatData: ChatDetailsResponse;
  chatId: string;
}

export const SettingsTabPanel: React.FC<SettingsTabPanelProps> = ({
  chatData,
  chatId,
}) => {
  const { addNotification } = useNotification();
  const [isEnabled, setIsEnabled] = useState(chatData.isEnabled);

  // Update enabled state when chatData changes
  useEffect(() => {
    setIsEnabled(chatData.isEnabled);
  }, [chatData.isEnabled]);

  const { updateChatEnabled, isUpdating: isTogglingEnabled } =
    useUpdateChatEnabled({
      onSuccess: () => {
        // State will be updated via useEffect when chatData updates
      },
    });

  const handleEnabledToggle = useCallback(
    async (enabled: boolean) => {
      await updateChatEnabled({ id: chatId, enabled });
    },
    [chatId, updateChatEnabled]
  );

  // Initialize form with current chat data
  const formMethods = useForm<ChatSettingsFormData>({
    defaultValues: {
      chatName: chatData.chatName || '',
      restaurantName: chatData.restaurantName || '',
      deliveryAddress: chatData.address || '',
      additionalEmails: formatEmailsToString(chatData.additionalEmails),
      customerNumber: chatData.customerNumber || '',
      chatType:
        getChatTypeOptionFromChatType(chatData.type) || CHAT_TYPE_OPTIONS[0],
      // Seed with the chat's currently assigned custom catalog(s)
      subCatalog: (chatData.subCatalogs ?? []).map((subCatalog) => ({
        value: subCatalog.id,
        label: subCatalog.name,
        supplierId: chatData.supplierEntityId ?? '',
      })),
      createdContext: null, // Will be populated if needed
      // Seed the customer-specific MOV from the chat's current override (unset
      // when the chat only inherits the supplier-level default).
      customerLevelMovAmount: chatData.movSettings?.amount ?? '',
      customerLevelMovCurrency: chatData.movSettings?.currency ?? null,
      visibleChatPrices: false,
      isPriceOverrideEnabled: false,
    },
  });

  // Get prices visibility (and a refetch to reconcile after a successful save).
  const { pricesVisibility, refetch: refetchPricesVisibility } =
    useChatPricesVisibility({
      chatId,
    });

  // Seed the price-override controls from the server read. This moves the RHF
  // *baseline* (defaultValues) to server truth — not just the value — so a chat
  // that loads with an existing override has its baseline at `true`, and
  // unchecking the override is genuinely dirty (change detection fires). The
  // previous `setValue(..., { shouldDirty: false })` left the baseline at the
  // mount default `false`, so unchecking an existing override equalled the
  // baseline and the save was a silent no-op. Seeding is skipped while the
  // operator has an in-progress edit so a refetch never clobbers it.
  useEffect(() => {
    if (!pricesVisibility) return;
    if (pricesVisibility.__typename !== 'ChatPricesVisibilitySuccess') return;

    seedPriceVisibilityBaseline(formMethods, pricesVisibility);
  }, [pricesVisibility, formMethods]);

  const { handleSubmit, formState } = formMethods;
  const { isDirty, dirtyFields } = formState;

  // The form is reset by handleSaveSettings only after *every* requested
  // operation succeeds — not per-hook — so a later failure can no longer wipe
  // the dirty state and falsely announce that everything saved.
  const { updateSettings, isUpdating } = useUpdateChatSettings();

  const { updateSubCatalogChats, isUpdating: isUpdatingSubCatalog } =
    useUpdateSubCatalogChats();

  const { updatePricesVisibility, isUpdating: isUpdatingPricesVisibility } =
    useUpdateChatPricesVisibility();

  const {
    updateMov,
    removeMov,
    isUpdating: isUpdatingMov,
  } = useUpdateSupplierBuyerMov();

  // Persist the customer-specific MOV (legacy parity), keyed by the distributor
  // entity + buyer. Amount and currency are a pair: clearing both removes the
  // override (falls back to the supplier default); setting both writes it. A
  // half-filled pair or a malformed amount is rejected (thrown) so the field
  // stays dirty and retryable instead of the edit being silently dropped.
  const saveCustomerLevelMov = useCallback(
    async (
      data: ChatSettingsFormData,
      supplierEntityId: string,
      buyerId: string
    ) => {
      // Validation errors aren't surfaced by a hook — reject them here (with a
      // toast) so the MOV field stays dirty instead of the edit being dropped.
      let operation;
      try {
        operation = resolveCustomerLevelMov(
          data.customerLevelMovAmount,
          data.customerLevelMovCurrency
        );
      } catch (validationError: unknown) {
        const message =
          validationError instanceof InvalidMovAmountError
            ? validationError.message
            : 'Invalid minimum order value.';
        addNotification({
          title: 'Invalid minimum order value',
          message,
          type: 'error',
        });
        throw validationError;
      }

      // Mutation failures propagate (the hook already toasted them).
      if (operation.action === 'remove') {
        await removeMov({ supplierEntityId, buyerId });
      } else {
        await updateMov({
          supplierEntityId,
          buyerId,
          amount: operation.amount,
          currency: operation.currency,
        });
      }
    },
    [updateMov, removeMov, addNotification]
  );

  const handleSaveSettings = useCallback(
    async (data: ChatSettingsFormData) => {
      // Model the submission as explicit requested operations. Each owns the
      // form fields it persists and a runner that resolves on success or
      // rejects on ANY failure (GraphQL, business-union, network, validation).
      // `commit` marks that operation's fields clean; it is invoked only on a
      // partial-failure save so failed fields stay dirty and retryable, while
      // `runRequestedSaveOperations` resets the whole form only when every
      // requested operation succeeds.
      // Each operation's `commit` baselines the SUBMITTED values for its fields
      // (the `data` snapshot), never the live `getValues()` — an edit typed
      // mid-save must stay dirty afterwards, not be baptized as the clean saved
      // baseline.
      // The chat-service `updateChat` drops a falsy customerNumber from the
      // write (a deliberate GSI NULL-key guard), so clearing it is a silent
      // server-side no-op — the old value survives. Rather than baptize the
      // cleared field clean (form empty, server unchanged → divergence on
      // reload), warn the operator and re-baseline the field to the value the
      // server kept. `submitData` carries the effective customerNumber (server
      // truth when a clear is blocked) so commit/reset re-baseline to it, not to
      // the submitted empty string.
      const customerNumberClear = resolveCustomerNumberClear(
        data.customerNumber,
        chatData.customerNumber
      );
      const submitData: ChatSettingsFormData = {
        ...data,
        customerNumber: customerNumberClear.effectiveCustomerNumber,
      };
      if (customerNumberClear.isBlockedClear) {
        addNotification({
          title: 'Customer number kept',
          message:
            'Customer number cannot be cleared — the previous value was kept.',
          type: 'warning',
        });
        // Re-seed the visible field to server truth and clear its dirty state,
        // even when no operation runs (this is the only change on the form).
        formMethods.resetField('customerNumber', {
          defaultValue: customerNumberClear.effectiveCustomerNumber,
        });
      }

      const commitFields = (fields: Array<keyof ChatSettingsFormData>) => () =>
        commitSubmittedFields(formMethods, submitData, fields);

      const operations: RequestedSaveOperation[] = [];

      // 1. Chat details. A blocked customerNumber clear does not, on its own,
      // count as a change to persist (the backend would ignore it) — the field
      // has already been re-baselined above.
      const chatDetailsDirty =
        Boolean(dirtyFields.chatName) ||
        Boolean(dirtyFields.restaurantName) ||
        Boolean(dirtyFields.deliveryAddress) ||
        Boolean(dirtyFields.additionalEmails) ||
        (Boolean(dirtyFields.customerNumber) &&
          !customerNumberClear.isBlockedClear);
      if (chatDetailsDirty) {
        operations.push({
          commit: commitFields([
            'chatName',
            'restaurantName',
            'deliveryAddress',
            'additionalEmails',
            'customerNumber',
          ]),
          run: () =>
            updateSettings({
              chatId,
              chat: {
                chatName: submitData.chatName,
                restaurantName: submitData.restaurantName,
                deliveryAddress: submitData.deliveryAddress,
                // split → trim → drop empties so an empty field sends [] rather
                // than [""] (the backend stores additionalEmails verbatim and
                // feeds them to cc consumers — order transmission, payments).
                additionalEmails: parseEmailsFromString(
                  submitData.additionalEmails
                ),
                customerNumber: submitData.customerNumber,
              },
            }),
        });
      }

      // 2. Custom-catalog assignment — diffed against the chat's current set and
      // persisted via updateSubCatalogChats (the catalog-service mutation the
      // legacy admin uses), not the chat update mutation.
      const currentSubCatalogIds = (chatData.subCatalogs ?? []).map(
        (subCatalog) => subCatalog.id
      );
      const selectedSubCatalogIds = (data.subCatalog ?? []).map(
        (option) => option.value
      );
      const { createIds, removeIds } = getSubCatalogChanges(
        currentSubCatalogIds,
        selectedSubCatalogIds
      );
      if (createIds.length > 0 || removeIds.length > 0) {
        operations.push({
          commit: commitFields(['subCatalog']),
          run: () => updateSubCatalogChats({ chatId, createIds, removeIds }),
        });
      }

      // 3. Price-visibility override. Fire only when the operator touched the
      // controls (the initial load seeds them with shouldDirty:false). Enabling
      // sends the override value; disabling omits displayPricesOverride so the
      // backend clears it back to NULL (Supplier-level fallback).
      const priceOverrideChanged =
        Boolean(dirtyFields.isPriceOverrideEnabled) ||
        Boolean(dirtyFields.visibleChatPrices);
      if (priceOverrideChanged) {
        operations.push({
          commit: commitFields(['isPriceOverrideEnabled', 'visibleChatPrices']),
          run: () =>
            updatePricesVisibility({
              chatId,
              ...(data.isPriceOverrideEnabled
                ? { displayPricesOverride: data.visibleChatPrices ?? true }
                : {}),
            }),
          // Price visibility lives in its own query, not GetChatData; refetch it
          // to reconcile the authoritative override after a successful write.
          reconcile: () => refetchPricesVisibility(),
        });
      }

      // 4. Customer-specific MOV.
      const movChanged =
        Boolean(dirtyFields.customerLevelMovAmount) ||
        Boolean(dirtyFields.customerLevelMovCurrency);
      const { supplierEntityId } = chatData;
      const buyerId = chatData.buyerEntity?.id;
      if (movChanged && (!supplierEntityId || !buyerId)) {
        // The operator changed the MOV but the chat is missing the distributor
        // entity or buyer id needed to key the write. Silently dropping the op
        // and letting the rest of the save reset the form would baptize the
        // never-saved MOV fields clean. Surface the failure and abort the WHOLE
        // submit (no operation has run yet) so every field — MOV included —
        // stays dirty and retryable, mirroring how malformed MOV input aborts.
        addNotification({
          title: 'Cannot update minimum order value',
          message:
            'This chat is missing its distributor or buyer, so the minimum order value cannot be saved.',
          type: 'error',
        });
        return;
      }
      if (movChanged && supplierEntityId && buyerId) {
        operations.push({
          commit: commitFields([
            'customerLevelMovAmount',
            'customerLevelMovCurrency',
          ]),
          run: () => saveCustomerLevelMov(data, supplierEntityId, buyerId),
        });
      }

      const outcome = await runRequestedSaveOperations(operations, () =>
        // Every requested operation succeeded: the SUBMITTED values become the
        // new clean baseline, while any edit typed mid-save stays visible and
        // dirty rather than being dropped while shown as saved.
        resetToSubmitted(formMethods, submitData)
      );

      // A dirty form can still map to zero operations (e.g. the catalog was
      // toggled then reverted to its original set). Surface an info toast so
      // the click is not a silent dead-end; Save stays enabled. Skip it when a
      // blocked customerNumber clear already surfaced its own warning, so the
      // operator does not get a contradictory "no changes" message.
      if (outcome.requested === 0 && !customerNumberClear.isBlockedClear) {
        addNotification({
          title: 'No changes to save',
          message: 'There are no changes to save.',
          type: 'info',
        });
      }
    },
    [
      chatId,
      chatData,
      dirtyFields.chatName,
      dirtyFields.restaurantName,
      dirtyFields.deliveryAddress,
      dirtyFields.additionalEmails,
      dirtyFields.customerNumber,
      dirtyFields.isPriceOverrideEnabled,
      dirtyFields.visibleChatPrices,
      dirtyFields.customerLevelMovAmount,
      dirtyFields.customerLevelMovCurrency,
      formMethods,
      updateSettings,
      updateSubCatalogChats,
      updatePricesVisibility,
      saveCustomerLevelMov,
      refetchPricesVisibility,
      addNotification,
    ]
  );

  const handleCancel = useCallback(() => {
    formMethods.reset();
  }, [formMethods]);

  const hasSupplier = !!chatData.supplierEntity;
  // Check if any supplier user requires customer number
  const supplierRequiresCustomerNumber =
    chatData.users?.some(
      (user) => user.supplier && user.orderPreferences?.whatsApp
    ) ?? false;

  return (
    <div className="space-y-6">
      {/* Chat Enabled Toggle */}
      <Card className="border border-default-200">
        <CardBody className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                <FiPower className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  Chat Status
                </h3>
                <p className="text-sm text-default-500">
                  {isEnabled
                    ? 'This chat is currently enabled and active'
                    : 'This chat is currently disabled'}
                </p>
              </div>
            </div>
            <Switch
              isSelected={isEnabled}
              onValueChange={handleEnabledToggle}
              isDisabled={isTogglingEnabled}
              size="lg"
              color={isEnabled ? 'success' : 'default'}
              classNames={{
                wrapper: isEnabled ? 'bg-success-500' : 'bg-default-300',
              }}
              aria-label={isEnabled ? 'Disable chat' : 'Enable chat'}
            />
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="space-y-8">
          <PageHeader
            title="Settings"
            subtitle="Configure chat details, delivery settings, and preferences."
            icon={<FiSettings className="w-6 h-6 text-primary" />}
          />

          <FormProvider {...formMethods}>
            <form
              onSubmit={handleSubmit(handleSaveSettings)}
              className="space-y-6"
            >
              {/* Basic Chat Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground">
                  Basic Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ChatFormInputField name="chatName" isRequired />
                  <ChatFormInputField name="restaurantName" />
                </div>

                <ChatFormInputField name="deliveryAddress" />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ChatFormInputField name="additionalEmails" type="email" />
                  <ChatFormInputField
                    name="customerNumber"
                    customerNumberMandatory={supplierRequiresCustomerNumber}
                  />
                </div>

                <CustomerNumberWarning
                  chatId={chatId}
                  supplierEntityId={chatData.supplierEntityId}
                  savedCustomerNumber={chatData.customerNumber}
                />
              </div>

              {/* Chat Configuration */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground">
                  Configuration
                </h3>

                <ChatTypeSelect disabled />

                <SubCatalogSelect supplierId={chatData.supplierEntityId} />

                <VisibleChatPricesSection />
                {hasSupplier && <CustomerLevelMovSection />}
                {/* TODO: Add ChatCreatedContextField when marketplace feature is enabled */}
              </div>

              {/* Delivery Settings */}
              {!hasSupplier && (
                <Card className="border-warning-200 bg-warning-50">
                  <CardBody>
                    <div className="space-y-2">
                      <h4 className="font-semibold text-warning-800">
                        Delivery days and cutoff times
                      </h4>
                      <p className="text-sm text-warning-700">
                        Add a distributor entity to this chat in order to define
                        chat specific delivery days and cutoff times.
                      </p>
                    </div>
                  </CardBody>
                </Card>
              )}

              {/* Delivery Settings */}
              {hasSupplier && <DeliverySettingsSection chatId={chatId} />}

              {/* Form Actions */}
              <FormFooter
                onCancel={handleCancel}
                onSave={handleSubmit(handleSaveSettings)}
                isSaveLoading={
                  isUpdating ||
                  isUpdatingSubCatalog ||
                  isUpdatingPricesVisibility ||
                  isUpdatingMov
                }
                isSaveDisabled={!isDirty}
              />
            </form>
          </FormProvider>
        </CardBody>
      </Card>
    </div>
  );
};
